/**
 * axios setup to use real API service
 */

import axios, { AxiosRequestConfig } from 'axios';
import { API_URL } from 'runtime-config';

const axiosServices = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Send cookies with requests (for HTTP-only refresh token)
});

// ==============================|| AXIOS - FOR REAL API SERVICES ||============================== //

axiosServices.interceptors.request.use(
  async (config) => {
    const accessToken = localStorage.getItem('serviceToken');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're currently refreshing to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosServices.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh for auth endpoints - these 401s are legitimate auth failures
    // Also skip for /auth/refresh to prevent infinite loop when user is not logged in
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') ||
                           originalRequest?.url?.includes('/auth/register') ||
                           originalRequest?.url?.includes('/auth/refresh') ||
                           originalRequest?.url?.includes('/auth/logout') ||
                           originalRequest?.url?.includes('/auth/verify-email') ||
                           originalRequest?.url?.includes('/auth/reset-password') ||
                           originalRequest?.url?.includes('/auth/forgot-password') ||
                           originalRequest?.url?.includes('/auth/verify-reset-token');

    // If error is 401 and we haven't tried to refresh yet (skip for auth endpoints)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return axiosServices(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token (refresh token is sent via HTTP-only cookie)
        const baseUrl = API_URL.replace(/\/?$/, '/');
        const response = await axios.post(
          baseUrl + 'auth/refresh',
          {}, // Empty body - refresh token is in cookie
          { withCredentials: true } // Send cookies
        );

        const responseData = response.data.response || response.data;
        const { tokens } = responseData;

        // Update stored access token (refresh token stays in cookie)
        localStorage.setItem('serviceToken', tokens.accessToken);
        axiosServices.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`;

        // Process queued requests
        processQueue(null, tokens.accessToken);
        isRefreshing = false;

        // Retry original request
        originalRequest.headers['Authorization'] = `Bearer ${tokens.accessToken}`;
        return axiosServices(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        localStorage.removeItem('serviceToken');
        // Dispatch Redux logout to clear auth state and redirect to login
        // Use dynamic import to avoid circular dependency (store slices import from this file)
        import('store').then(({ store }) => {
          import('store/slices/auth').then(({ logout }) => {
            store.dispatch(logout());
          });
        }).catch(() => {}); // Silently fail if store not available
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosServices;

export async function fetcher(args: string | [string, AxiosRequestConfig]) {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.get(url, { ...config });

  return res.data;
}
